import React, { useState, useEffect, useRef } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import { Auth, API } from "aws-amplify";
import { CSVLink } from 'react-csv';
import moment from 'moment';
import ReactTable from "react-table";
import 'react-table/react-table.css';

const CSV_HEADERS = [
    { label: 'ID', key: 'transactionID' },
    { label: 'Date', key: 'transactionDate' },
    { label: 'Description', key: 'name' },
    { label: 'Amount', key: 'cost' }
];

const COLUMNS = [
    { Header: 'ID', accessor: 'transactionID' },
    { Header: 'Transaction Date', accessor: 'transactionDate' },
    { Header: 'Road Test Date', accessor: 'roadTestDate' },
    { Header: 'Description', accessor: 'name' },
    { Header: 'Amount', accessor: 'cost' }
];

export default function ExportTransactions() {
    const [loading, setLoading] = useState(false);
    const [fetchedData, setFetchedData] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [yearOptions, setYearOptions] = useState([]);
    const [filterYear, setFilterYear] = useState('');
    const [dateError, setDateError] = useState(null);
    const [refreshStatus, setRefreshStatus] = useState(null);

    const reactTableTransaction = useRef(null);
    const filterCSVLink = useRef(null);

    useEffect(() => {
        const currentYear = moment().year();
        const options = Array.from({ length: currentYear - 2017 }, (_, i) => String(2018 + i));
        setYearOptions(options);
        setFilterYear(moment().format('YYYY'));
    }, []);

    const handleChange = (e) => {
        const value = e.target.value;
        setFilterYear(value);
        setFetchedData(false);
        setFilteredData([]);
        setRefreshStatus(null);
        setDateError(null);
    };

    const getFilteredStudents = async () => {
        setLoading(true);
        setFetchedData(true);
        setDateError(null);
        setRefreshStatus(null);

        try {
            const session = await Auth.currentSession();
            const options = {
                body: { date: filterYear },
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': session.idToken.jwtToken
                }
            };

            let fetched = await API.post('APIGateway', '/v2getStudentsByFilter', options);
            setLoading(false);

            if (fetched.length === 0) {
                setDateError('No transactions found for the selected year.');
                setFilteredData([]);
                return;
            }

            fetched = fetched.map(x => {
                const scheduledLesson = (x.selectedPackage || '').includes('Road Test Only')
                    ? 'N/A'
                    : String(x.scheduledLesson);

                const cost = x.hasOwnProperty('fullPricing')
                    ? x.fullPricing.map(p => p.price).reduce((a, b) => a + b, 0)
                    : (() => { console.log(x); return 'MISSING PRICING DATA'; })();

                const year = moment(x.timestamp).format('YYYY');
                const name = x.fName + ' ' + x.lName;
                const rawDate = x.date;
                const date = x.weekendDate === 'Sunday'
                    ? moment(rawDate).add(1, 'days').format('MM-DD-YYYY (dddd)')
                    : moment(rawDate).format('MM-DD-YYYY (dddd)');
                const transactionDate = moment(x.timestamp).format('MM-DD-YYYY');
                const roadTestDate = x.weekendDate === 'Sunday'
                    ? moment(rawDate).add(1, 'days').format('MM-DD-YYYY')
                    : moment(rawDate).format('MM-DD-YYYY');

                return { ...x, scheduledLesson, cost, year, name, date, transactionDate, roadTestDate };
            });

            fetched.sort((a, b) => b.date < a.date ? 1 : b.date > a.date ? -1 : 0);

            const filtered = fetched.filter(x => x.year === filterYear);
            setFilteredData(filtered);
            setRefreshStatus(`${filtered.length} transaction${filtered.length === 1 ? "" : "s"} loaded.`);
        } catch (error) {
            console.log(error);
            setLoading(false);
            if (error.response?.status === 401) {
                setDateError('Authentication Failed. Please log back in.');
            } else {
                setDateError('There was an error loading transactions. Please try again or contact your developer. ' + JSON.stringify(error.response));
            }
        }
    };

    const handleFilterDownload = async () => {
        setLoading(true);
        const sorted = reactTableTransaction.current.getResolvedState().sortedData;
        setFilteredData(sorted);
        setTimeout(() => {
            filterCSVLink.current.link.click();
            setLoading(false);
        }, 0);
    };

    return (
        <div className="holder">
            {/* Alerts */}
            {dateError && (
                <Alert variant="warning" onClose={() => setDateError(null)} dismissible>
                    <strong>{dateError}</strong>
                </Alert>
            )}
            {refreshStatus && (
                <Alert variant="info" onClose={() => setRefreshStatus(null)} dismissible style={{ padding: "0.4rem 0.75rem" }}>
                    {refreshStatus}
                </Alert>
            )}

            {/* Page header */}
            <div style={{ marginBottom: "1.25rem" }}>
                <h3 style={{ marginBottom: "0.25rem" }}>View Yearly Transaction Data</h3>
                <small className="text-muted">Select a year to load transaction records. Filter the table and download as CSV.</small>
            </div>

            {/* Year selector */}
            <Card style={{ marginBottom: "1.5rem" }}>
                <Card.Body>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: "0.75rem", flexWrap: "wrap" }}>
                        <Form.Group controlId="filterYear" style={{ margin: 0, minWidth: "140px" }}>
                            <Form.Label style={{ marginBottom: "0.25rem" }}><strong>Year</strong></Form.Label>
                            <Form.Control
                                as="select"
                                name="selectedYear"
                                onChange={handleChange}
                                value={filterYear}
                            >
                                {yearOptions.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <Button
                            variant="primary"
                            disabled={loading || filterYear.length === 0}
                            onClick={getFilteredStudents}
                        >
                            {loading && !filteredData.length
                                ? <><Spinner animation="border" size="sm" /> Loading…</>
                                : "Fetch Transactions"
                            }
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {/* Results */}
            {fetchedData && (
                loading && filteredData.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "3rem 0", color: "#6c757d" }}>
                        <Spinner animation="border" role="status" />
                        <div style={{ marginTop: "0.75rem" }}>Loading transactions…</div>
                    </div>
                ) : filteredData.length > 0 ? (
                    <Card>
                        <Card.Header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
                            <span>
                                <strong>Transactions</strong>
                                <Badge variant="secondary" style={{ marginLeft: "0.5rem" }}>{filteredData.length}</Badge>
                            </span>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <small className="text-muted">Filters applied to the table are reflected in the CSV export.</small>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={loading}
                                    onClick={handleFilterDownload}
                                >
                                    {loading ? <><Spinner animation="border" size="sm" /> Preparing…</> : "Download CSV"}
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body style={{ padding: 0 }}>
                            <CSVLink
                                ref={filterCSVLink}
                                data={filteredData}
                                headers={CSV_HEADERS}
                                className="hidden"
                                filename={filterYear + 'parkwaydrivingschoolexport.csv'}
                            />
                            <ReactTable
                                ref={reactTableTransaction}
                                defaultSorted={[{ id: 'transactionDate', desc: false }]}
                                data={filteredData}
                                columns={COLUMNS}
                                minRows={0}
                                defaultPageSize={250}
                                pageSizeOptions={[5, 10, 20, 25, 50, 100, 250]}
                            />
                        </Card.Body>
                    </Card>
                ) : (
                    <Card body className="text-center text-muted">
                        No transactions found for {filterYear}.
                    </Card>
                )
            )}
        </div>
    );
}
